import type { ActionIconVariant } from '@mantine/core'
import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef, sizes } from '@shared/port-v1.0.0'

export type Props = BaseReactProps & {
	iconName: string
	variant: ActionIconVariant
	iconSize: string
	iconStroke: string
	iconColor: string
	disabled: boolean
	loading: boolean
}

import Comp from '../component'

export default {
	hashTag: '#pre-release',
	module: { static: Comp },
	inNode: {
		inputs: [
			getPortDef({
				name: 'iconName',
				displayName: 'Icon name',
				group: 'Custom',
				customGroup: 'Icon', 
				type: 'string',
				default: 'ghost',
			}),
			getPortDef({
				name: 'iconSize',
				displayName: 'Icon size',
				group: 'Custom',
				customGroup: 'Icon',
				type: 'number',
				default: 24,
				dependsOn: (p: Props) => Boolean(p.iconName),
			}),
			getPortDef({
				name: 'iconStroke',
				displayName: 'Icon stroke',
				group: 'Custom',
				customGroup: 'Icon',
				type: 'string',
				default: '2',
				dependsOn: (p: Props) => Boolean(p.iconName),
			}),
			getPortDef({
				name: 'iconColor',
				displayName: 'Icon color',
				group: 'Custom',
				customGroup: 'Icon',
				type: 'string',
				default: 'gray',
				dependsOn: (p: Props) => Boolean(p.iconName),
			}),
			getPortDef({ name: 'size', displayName: 'Size', group: 'Dimensions', type: sizes, default: 'md' }),
			getPortDef({ name: 'radius', displayName: 'Radius', group: 'Styles', type: sizes, default: 'md' }),
			getPortDef({ name: 'color', displayName: 'Color', group: 'Styles', type: 'string', default: 'gray' }),
			getPortDef({
				name: 'variant',
				displayName: 'Variant',
				group: 'Styles',
				type: [
					{ value: 'default', label: 'Default' },
					{ value: 'filled', label: 'Filled' },
					{ value: 'subtle', label: 'Subtle' },
					{ value: 'outline', label: 'Outline' },
					{ value: 'light', label: 'Light' },
					{ value: 'transparent', label: 'Transparent' },
					{ value: 'white', label: 'White' },
					{ value: 'gradient', label: 'Gradient' },
				],
				default: 'subtle',
			}),
			getPortDef({
				name: 'gradient',
				displayName: 'Gradient',
				group: 'Styles',
				type: 'objectEval',
				dependsOn: (p: Props) => p.variant === 'gradient',
				codeComment: `//(props) => { return { from: 'blue', to: 'cyan', deg: 90 } }`,
			}),
			getPortDef({
				name: 'loaderProps',
				displayName: 'Loader props',
				group: 'Styles',
				type: 'objectEval',
				codeComment: `//(props) => { return { type: 'dots' } }`,
			}),
			getPortDef({ name: 'disabled', displayName: 'Disabled', group: 'States', type: 'boolean', default: false }),
			getPortDef({ name: 'loading', displayName: 'Loading', group: 'States', type: 'boolean', default: false }),
		],
		outputs: [getPortDef({ name: 'clicked', displayName: 'Clicked', group: 'Signals', type: 'signal' })],
	},
} satisfies ReactNodeDef
